import { Link } from 'react-router-dom';
import { useAchievements, useFlag, usePass, usePassVouchers, usePassWardrobe, useProfile } from '../api/hooks';
import { Loader, ErrorView } from '../components/StateViews';
import { CosmeticAvatar } from '../components/cosmetics/CosmeticAvatar';
import { NickFx } from '../components/cosmetics/NickFx';
import { ProfileBg } from '../components/cosmetics/ProfileBg';
import { Watermark } from '../components/Watermark';
import { GearIcon } from '../components/icons';
import { ShowcaseCard } from '../components/achievements/ShowcaseCard';
import { RatingCard } from '../components/rating/RatingCard';
import { RatingChart } from '../components/rating/RatingChart';
import { RatingHowItWorks } from '../components/rating/RatingHowItWorks';
import { usePersonalRating } from '../api/hooks';
import { plural, shortDate, seasonLabel } from '../format';

export function ProfileScreen() {
  const { data, isLoading, isError, error } = useProfile(null);
  // Тот же запрос, что у витрины ниже, — из кэша, без второго похода в API.
  const loyalty = useAchievements(null).data?.loyalty ?? null;
  const rating = usePersonalRating(null).data ?? null;
  const passEnabled = useFlag('pass');
  const pass = usePass(passEnabled).data ?? null;
  const wardrobe = usePassWardrobe(passEnabled).data ?? null;
  const vouchers = usePassVouchers(passEnabled);

  if (isLoading) return <Loader />;
  if (isError || !data) return <ErrorView message={(error as Error)?.message} />;

  const { rank, points, wins, gamesPlayed, itmPercent } = data.stats;

  return (
    <div>
      <Watermark />

      {/* Фон идёт до верха и краёв экрана, но не ниже блока личного рейтинга */}
      <div className="profile-hero pbg-host">
        <ProfileBg cosmetic={data.cosmetics?.bg} parallax />
        <div className="profile-topbar">
          <div className="title">Профиль</div>
          <Link to="/settings" className="iconbtn" aria-label="Настройки" style={{ color: 'var(--gold)' }}>
            <GearIcon />
          </Link>
        </div>

        <div className="profile-head">
        <Link to="/settings/edit" className="profile-avatar-wrap" aria-label="Изменить фото и имя">
          <CosmeticAvatar name={data.displayName} src={data.photoUrl} size={92} frame={data.cosmetics?.frame} bordered />
          {/* макет 6.0b: бейдж — только «ур. N», место уходит в подстроку */}
          {(rank || (passEnabled && pass?.me)) && (
            <div className="rank-badge">
              {passEnabled && pass?.me ? `ур. ${pass.me.level}` : `#${rank}`}
            </div>
          )}
        </Link>
        <div className="profile-name">
          <NickFx cosmetic={data.cosmetics?.nick}>{data.displayName}</NickFx>
        </div>
        <div className="profile-sub">
          {rank ? `#${rank} · ` : ''}
          {points} баллов{data.seasonId ? ` · ${seasonLabel(data.seasonId)}` : ''}
        </div>
        {loyalty && (
          <Link to="/achievements" className="profile-loyalty">
            {loyalty.name}
          </Link>
        )}
        </div>
      </div>


      {/* Личный рейтинг (RA1) + график динамики — вторая ось прогресса */}
      <RatingCard pid={null} />
      {rating && rating.history.length >= 2 && (
        <RatingChart history={rating.history} levels={rating.levels} peakValue={rating.peak?.value ?? null} />
      )}
      <RatingHowItWorks />

      <ShowcaseCard pid={null} />

      {/* 6.0b: строки Straddle Pass в профиле (за флагом) */}
      {passEnabled && pass?.status === 'active' && pass.me && (
        <div className="bp-profile-rows">
          <Link className="bp-profile-row" to="/pass">
            {/* иконка 34px: номер уровня на золотом (макет 6.0b) */}
            <span className="ic ic--lvl mono">{pass.me.level}</span>
            <span className="mid">
              {/* Спека 6.0b: строка всегда называется Straddle Pass+ */}
              <span className="nm">Straddle Pass+</span>
              <span className="sub">
                {pass.me.claimable > 0
                  ? `${pass.me.claimable} ${plural(pass.me.claimable, ['награда ждёт', 'награды ждут', 'наград ждут'])} · `
                  : ''}
                {pass.season?.daysLeft}д до конца
              </span>
            </span>
            {pass.me.claimable > 0 && <i className="bp-dot" />}
            <span className="chev">›</span>
          </Link>
          <Link className="bp-profile-row" to="/pass/wardrobe">
            <span className="ic">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 6a2 2 0 1 1 2-2M12 6 3.6 12.6A1.6 1.6 0 0 0 4.6 15.4h14.8a1.6 1.6 0 0 0 1-2.8z" />
              </svg>
            </span>
            <span className="mid">
              <span className="nm">Гардероб</span>
              <span className="sub">
                {wardrobe
                  ? `${wardrobe.items.filter((i) => i.owned).length} предметов · надето ${
                      [wardrobe.equipped.frame, wardrobe.equipped.nick, wardrobe.equipped.row, wardrobe.equipped.bg].filter((x) => x != null).length
                    }`
                  : 'косметика сезона'}
              </span>
            </span>
            {(wardrobe?.items.filter((i) => i.isNew).length ?? 0) > 0 && (
              <b className="bp-row-chip">{wardrobe!.items.filter((i) => i.isNew).length} новых</b>
            )}
            <span className="chev">›</span>
          </Link>
          <Link className="bp-profile-row" to="/pass/decks">
            <span className="ic">
              <svg width="14" height="16" viewBox="0 0 24 30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <rect x="3" y="3" width="18" height="24" rx="3.5" />
                <path d="M9 10h6" opacity=".6" />
              </svg>
            </span>
            <span className="mid">
              <span className="nm">Колоды</span>
              <span className="sub">
                {pass.me.decksUnopened > 0 ? `${pass.me.decksUnopened} неоткрытых · ` : ''}магазин
              </span>
            </span>
            <span className="chev">›</span>
          </Link>
          {(vouchers.data ?? []).length > 0 && (
            <Link className="bp-profile-row" to="/pass/vouchers">
              <span className="ic">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden>
                  <path d="M4 8a2 2 0 0 0 2-2h12a2 2 0 0 0 2 2v3a2 2 0 0 0 0 2v3a2 2 0 0 0-2 2H6a2 2 0 0 0-2-2v-3a2 2 0 0 0 0-2z" />
                </svg>
              </span>
              <span className="mid">
                <span className="nm">Ваучеры</span>
                <span className="sub">
                  {(vouchers.data ?? []).filter((v) => v.status !== 'redeemed').length} активных
                </span>
              </span>
              <span className="chev">›</span>
            </Link>
          )}
        </div>
      )}

      <div className="stats-grid">
        <Link className="stat-tile stat-tile--profile" to="/me/tournaments?filter=wins">
          <div className="val">{wins}×</div>
          <div className="lbl">Победы</div>
        </Link>
        <Link className="stat-tile stat-tile--profile" to="/me/tournaments?filter=inmoney">
          <div className="val">{itmPercent}%</div>
          <div className="lbl">В очках</div>
        </Link>
        <Link className="stat-tile stat-tile--profile" to="/me/tournaments">
          <div className="val">{gamesPlayed}</div>
          <div className="lbl">Игр</div>
        </Link>
      </div>

      <div className="section-header">
        <h2>Последние 10 турниров</h2>
        <Link to="/me/tournaments" className="section-link">
          Все →
        </Link>
      </div>
      {data.recent.length === 0 ? (
        <div className="muted tiny">Пока нет сыгранных турниров</div>
      ) : (
        <div>
          <div className="lb-head">
            <span>Место · Турнир</span>
            <span>Очки</span>
          </div>
          {data.recent.map((r) => {
            const top = r.position !== null && r.position <= 3;
            return (
              <Link className="recent-row" key={r.gameId} to={`/tournaments/${r.gameId}/results`}>
                <div className={`pos-badge ${top ? 'pos-badge--top' : 'pos-badge--low'}`}>
                  {r.position ?? '—'}
                </div>
                <div className="info">
                  <div className="t">{r.title ?? r.format ?? 'Турнир'}</div>
                  <div className="d">{shortDate(r.startTime)}</div>
                </div>
                {r.points > 0 ? (
                  <div className="pts">+{r.points}</div>
                ) : (
                  <div className="pts pts--none">без очков</div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
